import Head from 'next/head';
import { usePathname } from 'next/navigation';

import { RouteData } from '@sitecore-jss/sitecore-jss-nextjs';
import { useSitecoreContext } from '@xsf/shared-lib/hooks/sitecore';

import { keysToCamelCase } from './keys-camel-case';
import { MetadataFieldsProps, MetadataProps } from './metadata.types';

export const OASMetadata: React.FC<MetadataFieldsProps> = ({ fields }) => {
  const { sitecoreContext } = useSitecoreContext();
  // TODO: remove this function when the BE is updated to return camelCase keys
  const route = keysToCamelCase(sitecoreContext.route as RouteData) as MetadataProps['route'];

  const pathname = usePathname();

  if (!fields) {
    return null;
  }
  const { siteName, canonical, customBrowserTitle, customMetaDescription, alternates } = fields;
  const faviconSrc = sitecoreContext?.theme?.favicon?.value?.src;

  const {
    browserTitle,
    metaDescription,
    metaKeywords,
    combineSiteTitle,
    canFollow,
    canIndex,
    openGraphTitle,
    openGraphDescription,
    pageImage,
  } = route.fields;

  // use marketPage related data first,
  // and fall back to route fields
  const activeBrowserTitle = fields?.marketPageBrowserTitle ?? browserTitle;
  const activeCombineSiteTitle = fields?.marketPageCombineSiteTitle ?? combineSiteTitle;
  const activeMetaDescription = fields?.marketPageMetaDescription ?? metaDescription;
  const activeMetaKeywords = fields?.marketPageMetaKeywords ?? metaKeywords;

  // Title logic
  const baseTitle = customBrowserTitle?.value || activeBrowserTitle?.value;
  const combinedTitle = [baseTitle, siteName?.value].filter((e) => e).join(' | ');
  const title = activeCombineSiteTitle?.value ? combinedTitle : baseTitle;

  // Description logic
  const description = customMetaDescription?.value || activeMetaDescription?.value;

  // Robots
  const index = canIndex?.value ? 'index' : 'noindex';
  const follow = canFollow?.value ? 'follow' : 'nofollow';

  // Open graph logic
  const ogDescription = openGraphDescription?.value || description;
  const ogTitle = openGraphTitle?.value || title;

  return (
    <Head>
      {/* Robots */}
      <meta name="robots" content={`${follow}, ${index}`} />

      {/* Site metadata content */}
      {title && <title>{title}</title>}
      {description && <meta name="description" content={description} />}
      {activeMetaKeywords?.value && <meta name="keywords" content={activeMetaKeywords.value} />}

      {/* Canonical */}
      {canonical?.value && <link rel="canonical" href={canonical.value} />}

      {/* Lang Alternates */}
      {alternates?.value &&
        alternates.value.map((alternate) => (
          <link
            key={`${alternate?.hrefLang?.toLowerCase()}`}
            rel="alternate"
            hrefLang={alternate?.hrefLang}
            href={alternate?.url}
          />
        ))}

      {/* Favicon */}
      {faviconSrc && <link rel="shortcut icon" href={faviconSrc} type="image/x-icon" />}

      {/* Open Graph */}
      <meta property="og:type" content="Website" />
      {siteName?.value && <meta property="og:site_name" content={siteName.value} />}
      {ogTitle && <meta property="og:title" content={ogTitle} />}
      {ogDescription && <meta property="og:description" content={ogDescription} />}
      {pathname && <meta property="og:url" content={pathname} />}
      {pageImage?.value?.src && <meta property="og:image" content={pageImage.value.src} />}
      <meta name="VirtualFolder" content="/" />
    </Head>
  );
};
